import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Smartphone } from "lucide-react";
import { useApi, useMe, errMsg } from "@/lib/api";

const MIN = 15;
const inputCls = "w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";

const PwField = ({ id, label, value, onChange, show }) => (
  <label className="block">
    <span className="text-sm text-neutral-300">{label}</span>
    <input id={id} data-testid={id} type={show ? "text" : "password"} autoComplete="new-password" value={value}
      onChange={(e) => onChange(e.target.value)} className={`mt-1.5 ${inputCls}`} />
  </label>
);

function ChangePasswordCard() {
  const api = useApi();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const tooShort = pw.length > 0 && pw.length < MIN;
  const mismatch = pw2.length > 0 && pw !== pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < MIN) return toast.error(`Password must be at least ${MIN} characters`);
    if (pw !== pw2) return toast.error("The two passwords don't match");
    setBusy(true);
    try {
      const r = await api.post("/admin/account/password", { new_password: pw });
      const n = r.data.signed_out_sessions;
      toast.success(`Password changed${n ? ` — ${n} other device${n > 1 ? "s" : ""} signed out` : ""}`);
      setPw(""); setPw2("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md rounded-lg border border-white/10 bg-[#141414] p-5" data-testid="change-password-form">
      <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#FF5A00]" /><h2 className="text-lg font-semibold">Change password</h2></div>
      <p className="mt-1 text-sm text-neutral-400">You'll stay signed in here. All other devices will be signed out and you'll get a confirmation email.</p>
      <div className="relative mt-5 space-y-4">
        <PwField id="new-password-input" label="New password" value={pw} onChange={setPw} show={show} />
        <PwField id="confirm-password-input" label="Confirm new password" value={pw2} onChange={setPw2} show={show} />
        <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password-visibility"
          className="absolute right-3 top-9 text-neutral-500 hover:text-white" aria-label="Show passwords">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <p className={`mt-2 text-xs ${tooShort ? "text-red-400" : "text-neutral-500"}`} data-testid="password-length-hint">At least {MIN} characters ({pw.length}/{MIN})</p>
      {mismatch && <p className="mt-1 text-xs text-red-400" data-testid="password-mismatch">Passwords don't match</p>}
      <button type="submit" disabled={busy} data-testid="change-password-submit"
        className="mt-5 rounded-md bg-[#FF5A00] px-5 py-2 text-sm font-semibold text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
        {busy ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

function AppVersionCard() {
  const api = useApi();
  const [info, setInfo] = useState(null);
  const [mv, setMv] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/app-version").then((r) => { setInfo(r.data); setMv(r.data.min_version || ""); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (value) => {
    setBusy(true);
    try {
      await api.put("/admin/app-version", { min_version: value });
      toast.success(value ? `Renters on versions older than ${value} must update before using the app` : "Forced update turned off");
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const enforced = !!info?.min_version;

  return (
    <div className="max-w-3xl rounded-lg border border-white/10 bg-[#141414] p-5" data-testid="app-version-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold"><Smartphone className="h-5 w-5 text-[#FF5A00]" />Android app version control</h2>
          <p className="mt-1 text-sm text-neutral-400">Published build: <span className="text-white" data-testid="app-version-current">{info?.version || "…"}</span>{info ? ` · ${info.downloads_7d} downloads in 7 days` : ""}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${enforced ? "bg-red-50 text-red-700" : "bg-white/10 text-neutral-300"}`} data-testid="app-version-status">{enforced ? `Required ≥ ${info.min_version}` : "Optional updates"}</span>
      </div>
      <p className="mt-3 text-sm text-neutral-400">When a minimum version is set, renters on an older app see a blocking "Update required" screen and cannot continue until they install the new build. Use this for safety-critical fixes.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-sm text-neutral-300">Minimum version</span>
          <input data-testid="app-min-version-input" value={mv} onChange={(e) => setMv(e.target.value)} placeholder="e.g. 1.5.1" className={`mt-1.5 w-40 ${inputCls} pr-3`} />
        </label>
        <button onClick={() => save(mv.trim())} disabled={busy || !mv.trim()} data-testid="app-min-version-save" className="rounded-md bg-[#FF5A00] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Require this version</button>
        {info?.version && mv.trim() !== info.version && <button onClick={() => save(info.version)} disabled={busy} data-testid="app-min-version-latest" className="rounded-md border border-white/15 px-4 py-2 text-sm text-neutral-300">Require latest ({info.version})</button>}
        {enforced && <button onClick={() => save("")} disabled={busy} data-testid="app-min-version-clear" className="rounded-md border border-white/15 px-4 py-2 text-sm text-neutral-300">Turn off</button>}
      </div>
      {info?.updated_at && <p className="mt-3 text-xs text-neutral-500" data-testid="app-version-updated">Last changed {new Date(info.updated_at).toLocaleString("en-IN")}{info.updated_by ? ` by ${info.updated_by}` : ""}</p>}
    </div>
  );
}

export default function AdminSettings() {
  const { me } = useMe();
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="mt-1 text-sm text-neutral-400">Account: <span className="text-white" data-testid="settings-account-email">{me?.email || "…"}</span></p>
      </div>
      <AppVersionCard />
      <ChangePasswordCard />
    </div>
  );
}
